// Prüfansicht für einen gescannten Beleg.
//
// Hier entscheidet ein Mensch, was in den Bestand geht. Die KI hat gelesen,
// zugeordnet und vorgeschlagen — gebucht wird erst mit dem Knopf ganz unten.
//
// Die Vorauswahl ist bewusst zurückhaltend: nur Zeilen mit eindeutigem Treffer
// (EAN oder Artikelnummer) sind angehakt. Alles Vermutete steht sichtbar da,
// aber unangehakt. Wer schnell arbeiten will, klickt einmal auf „Alle
// übernehmen"; wer sorgfältig arbeiten will, geht Zeile für Zeile durch. Beides
// ist möglich, aber nichts passiert von allein.
import { useMemo, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import { euro } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Auswahl, Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';

export type ScanPosition = {
  nr: number;
  bezeichnung: string;
  artikelnummer: string;
  ean: string;
  menge: number;
  einheit: string;
  einkaufspreis: number;
  gesamtpreis: number;
  steuersatz: number;
  unsicher: boolean;
  /** Versand, Porto, Skonto — kein lagerfähiger Artikel. */
  keineWare?: boolean;
  produktId: string | null;
  produktName: string | null;
  bestandJetzt: number | null;
  zuordnung: 'sicher' | 'vermutet' | 'keiner' | 'keinArtikel';
  zuordnungGrund: string;
  zuordnungCode?: string;
  uebernehmen: boolean;
  anlegen: boolean;
  verkaufspreis?: number;
};

export type ScanVorgang = {
  id: string;
  status: string;
  belegart: string;
  belegnummer: string;
  datum: string;
  sicherheit: number;
  hinweis: string;
  testmodus: boolean;
  uebersprungen: string[];
  summen: { netto?: number | null; steuer?: number | null; brutto?: number | null; waehrung?: string | null };
  positionen: ScanPosition[];
  lieferant: {
    erkannt: Record<string, string | null>;
    lieferantId: string | null;
    lieferantName: string | null;
    zuordnung: 'sicher' | 'vermutet' | 'keiner';
    zuordnungGrund: string;
    zuordnungCode?: string;
  };
};

type Artikel = { id: string; name: string; artikelnummer?: string; bestand?: number; einheit?: string };
type Lieferant = { id: string; name: string };

// Belegart und Ausgabenkategorie kommen als Kennung vom Server und werden
// hier übersetzt — so bleibt die API sprachneutral.
const BELEGARTEN: Record<string, Schluessel> = {
  lieferschein: 'pruef.lieferschein',
  rechnung: 'pruef.eingangsrechnung',
  gutschrift: 'pruef.gutschrift',
  bestellung: 'pruef.bestellung',
  kassenbon: 'pruef.kassenbon',
  unbekannt: 'pruef.beleg',
};

const KATEGORIEN = ['wareneinkauf', 'buero', 'fahrzeug', 'werbung', 'kommunikation', 'raumkosten', 'sonstiges'] as const;

/**
 * Übersetzter Grund der Zuordnung. Der Server schickt eine Kennung und den
 * deutschen Klartext; kennt die Oberfläche die Kennung nicht — etwa weil der
 * Server neuer ist als das ausgelieferte Frontend —, steht der Klartext da,
 * statt einer leeren Zeile.
 */
const GRUND_SCHLUESSEL: Record<string, Schluessel> = {
  ean: 'pruef.grund.ean',
  nummer: 'pruef.grund.nummer',
  name: 'pruef.grund.name',
  ustId: 'pruef.grund.ustId',
  aehnlich: 'pruef.grund.aehnlich',
  keiner: 'pruef.grund.keiner',
  keineWare: 'pruef.grund.keineWare',
};

const KATEGORIE_SCHLUESSEL: Record<string, Schluessel> = {
  wareneinkauf: 'pruef.kat.wareneinkauf',
  buero: 'pruef.kat.buero',
  fahrzeug: 'pruef.kat.fahrzeug',
  werbung: 'pruef.kat.werbung',
  kommunikation: 'pruef.kat.kommunikation',
  raumkosten: 'pruef.kat.raumkosten',
  sonstiges: 'pruef.kat.sonstiges',
};

function grundText(t: (s: Schluessel) => string, code: string | undefined, klartext: string) {
  const schluessel = code ? GRUND_SCHLUESSEL[code] : undefined;
  return schluessel ? t(schluessel) : klartext;
}

export default function BelegPruefung({
  vorgang, artikel, lieferanten, darfBuchhaltung, beiFertig, beiAbbruch,
}: {
  vorgang: ScanVorgang;
  artikel: Artikel[];
  lieferanten: Lieferant[];
  darfBuchhaltung: boolean;
  beiFertig: (zusammenfassung: string) => void;
  beiAbbruch: () => void;
}) {
  const { t } = useSprache();
  const [zeilen, setZeilen] = useState<ScanPosition[]>(vorgang.positionen);
  const [lieferantModus, setLieferantModus] = useState<'vorhanden' | 'neu' | 'ohne'>(
    vorgang.lieferant.lieferantId ? 'vorhanden'
      : vorgang.lieferant.erkannt?.name ? 'neu' : 'ohne',
  );
  const [lieferantId, setLieferantId] = useState(vorgang.lieferant.lieferantId || '');
  const [neuerLieferant, setNeuerLieferant] = useState({
    name: vorgang.lieferant.erkannt?.name || '',
    strasse: vorgang.lieferant.erkannt?.strasse || '',
    plz: vorgang.lieferant.erkannt?.plz || '',
    ort: vorgang.lieferant.erkannt?.ort || '',
    ustId: vorgang.lieferant.erkannt?.ustId || '',
    email: vorgang.lieferant.erkannt?.email || '',
    telefon: vorgang.lieferant.erkannt?.telefon || '',
  });

  // Eine Ausgabe entsteht nur bei einem Beleg mit Betrag — ein Lieferschein
  // ohne Preise hat nichts zu buchen.
  const hatSumme = Number(vorgang.summen?.brutto) > 0;
  const [ausgabeAn, setAusgabeAn] = useState(darfBuchhaltung && hatSumme && vorgang.belegart !== 'lieferschein');
  const [ausgabe, setAusgabe] = useState({
    brutto: String(vorgang.summen?.brutto ?? ''),
    steuersatz: '19',
    kategorie: 'wareneinkauf',
    belegnummer: vorgang.belegnummer || '',
    datum: vorgang.datum || new Date().toISOString().slice(0, 10),
    zahlart: 'ueberweisung',
  });

  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const aendern = (nr: number, teil: Partial<ScanPosition>) =>
    setZeilen(z => z.map(p => (p.nr === nr ? { ...p, ...teil } : p)));

  const gewaehlt = zeilen.filter(z => z.uebernehmen);
  const bereit = gewaehlt.every(z => z.anlegen || z.produktId) && (gewaehlt.length > 0 || ausgabeAn);

  const summeGewaehlt = useMemo(
    () => gewaehlt.reduce((s, z) => s + z.menge * z.einkaufspreis, 0),
    [gewaehlt],
  );

  async function buchen() {
    setSendet(true);
    setFehler(null);
    try {
      const antwort = await api<{
        ergebnis: { gebucht: { name: string; menge: number }[]; angelegt: { name: string }[]; uebersprungen: string[]; ausgabeId: string | null };
      }>(`/ki/vorgaenge/${vorgang.id}/buchen`, {
        methode: 'POST',
        daten: {
          lieferant: lieferantModus === 'vorhanden' ? { modus: 'vorhanden', lieferantId }
            : lieferantModus === 'neu' ? { modus: 'neu', ...neuerLieferant }
              : { modus: 'ohne' },
          positionen: zeilen,
          ...(ausgabeAn ? {
            ausgabe: {
              anlegen: true,
              brutto: Number(String(ausgabe.brutto).replace(',', '.')),
              steuersatz: Number(ausgabe.steuersatz),
              kategorie: ausgabe.kategorie,
              belegnummer: ausgabe.belegnummer,
              datum: ausgabe.datum,
              zahlart: ausgabe.zahlart,
            },
          } : {}),
        },
      });

      const e = antwort.ergebnis;
      const teile = [
        e.gebucht.length ? `${e.gebucht.length} Position(en) gebucht` : null,
        e.angelegt.length ? `${e.angelegt.length} Artikel neu angelegt` : null,
        e.ausgabeId ? 'Ausgabe erfasst' : null,
      ].filter(Boolean);
      beiFertig(teile.join(' · ') || 'Nichts zu buchen');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Buchung ist fehlgeschlagen.');
    } finally {
      setSendet(false);
    }
  }

  const sicherheitFarbe = vorgang.sicherheit >= 0.85 ? 'gruen' : vorgang.sicherheit >= 0.6 ? 'gelb' : 'rot';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl dark:bg-slate-900">

        {/* Kopf */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-slate-900 dark:text-slate-100">
                {t('pruef.titel').replace('{art}', t(BELEGARTEN[vorgang.belegart] || 'pruef.beleg'))}
              </h2>
              <Abzeichen art={sicherheitFarbe}>
                {Math.round(vorgang.sicherheit * 100)} {t('pruef.erkannt')}
              </Abzeichen>
              {vorgang.testmodus && <Abzeichen art="gelb">{t('assistent.testmodus')}</Abzeichen>}
            </div>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              {[vorgang.belegnummer, vorgang.datum].filter(Boolean).join(' · ') || t('pruef.ohneNummer')}
            </p>
          </div>
          <button onClick={beiAbbruch} aria-label={t('aktion.schliessen')}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Icon name="schliessen" />
          </button>
        </div>

        {/* Inhalt */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {vorgang.sicherheit < 0.6 && (
            <Hinweis art="warnung" titel={t('pruef.schwerLesbar')}>
              {t('pruef.schwerLesbarText')}
            </Hinweis>
          )}
          {vorgang.hinweis && <Hinweis art="info">{vorgang.hinweis}</Hinweis>}
          {vorgang.uebersprungen?.length > 0 && (
            <Hinweis art="warnung">
              {t('pruef.nichtAusgewertet')}: {vorgang.uebersprungen.join(', ')} — {t('pruef.nurBilder')}.
            </Hinweis>
          )}

          {/* Lieferant */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('feld.lieferant')}</h3>
            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
              <div className="mb-3 flex flex-wrap gap-2">
                {(['vorhanden', 'neu', 'ohne'] as const).map(m => (
                  <button key={m} type="button" onClick={() => setLieferantModus(m)}
                          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                            lieferantModus === m
                              ? 'bg-marke-600 text-white'
                              : 'border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                    {m === 'vorhanden' ? t('pruef.vorhandener') : m === 'neu' ? t('pruef.neuAnlegen') : t('pruef.ohne')}
                  </button>
                ))}
              </div>

              {lieferantModus === 'vorhanden' && (
                <>
                  <Auswahl label={t('feld.lieferant')} value={lieferantId} onChange={e => setLieferantId(e.target.value)}>
                    <option value="">{t('pruef.bitteWaehlen')}</option>
                    {lieferanten.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Auswahl>
                  {vorgang.lieferant.zuordnungGrund && (
                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                      {grundText(t, vorgang.lieferant.zuordnungCode, vorgang.lieferant.zuordnungGrund)}
                    </p>
                  )}
                </>
              )}

              {lieferantModus === 'neu' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Feld label={t('feld.name')} value={neuerLieferant.name}
                          onChange={e => setNeuerLieferant(l => ({ ...l, name: e.target.value }))} />
                  </div>
                  <Feld label={t('feld.strasse')} value={neuerLieferant.strasse}
                        onChange={e => setNeuerLieferant(l => ({ ...l, strasse: e.target.value }))} />
                  <div className="grid grid-cols-[6rem_1fr] gap-2">
                    <Feld label={t('feld.plz')} value={neuerLieferant.plz}
                          onChange={e => setNeuerLieferant(l => ({ ...l, plz: e.target.value }))} />
                    <Feld label={t('feld.ort')} value={neuerLieferant.ort}
                          onChange={e => setNeuerLieferant(l => ({ ...l, ort: e.target.value }))} />
                  </div>
                  <Feld label={t('feld.ustId')} value={neuerLieferant.ustId}
                        onChange={e => setNeuerLieferant(l => ({ ...l, ustId: e.target.value }))} />
                  <Feld label={t('feld.email')} value={neuerLieferant.email}
                        onChange={e => setNeuerLieferant(l => ({ ...l, email: e.target.value }))} />
                </div>
              )}
            </div>
          </section>

          {/* Positionen */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('feld.positionen')}{' '}
                <span className="font-normal text-slate-500">
                  ({t('pruef.gewaehlt').replace('{a}', String(gewaehlt.length)).replace('{b}', String(zeilen.length))})
                </span>
              </h3>
              <div className="flex gap-1">
                {/* „Alle" meint alle Warenzeilen — Versand und Gebühren bleiben außen vor. */}
                <button type="button" onClick={() => setZeilen(z => z.map(p => (p.keineWare
                  ? p
                  : { ...p, uebernehmen: true, anlegen: p.anlegen || !p.produktId })))}
                        className="rounded-lg px-2.5 py-1 text-xs font-medium text-marke-700 hover:bg-marke-50 dark:text-marke-300 dark:hover:bg-marke-950">
                  {t('pruef.alleUebernehmen')}
                </button>
                <button type="button" onClick={() => setZeilen(z => z.map(p => ({ ...p, uebernehmen: false })))}
                        className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                  {t('pruef.keine')}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {zeilen.map(z => (
                <PositionsZeile key={z.nr} zeile={z} artikel={artikel} aendern={aendern} />
              ))}
              {!zeilen.length && (
                <p className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  {t('pruef.keinePositionen')}
                </p>
              )}
            </div>

            {gewaehlt.length > 0 && (
              <p className="mt-2 text-right text-sm text-slate-600 dark:text-slate-400">
                {t('pruef.warenwert')}: <strong className="text-slate-900 dark:text-slate-100">{euro(summeGewaehlt)}</strong>
              </p>
            )}
          </section>

          {/* Ausgabe */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('pruef.rechnungsbetrag')}</h3>
            {!darfBuchhaltung ? (
              <p className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {t('pruef.brauchtBuchhaltung')}
              </p>
            ) : (
              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
                <label className="flex cursor-pointer items-start gap-3">
                  <input type="checkbox" checked={ausgabeAn} onChange={e => setAusgabeAn(e.target.checked)}
                         className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('pruef.alsAusgabe')}
                    <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                      {t('pruef.alsAusgabeText')}
                    </span>
                  </span>
                </label>

                {ausgabeAn && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <Feld label={t('pruef.brutto')} inputMode="decimal" value={ausgabe.brutto}
                          onChange={e => setAusgabe(a => ({ ...a, brutto: e.target.value }))} />
                    <Auswahl label={t('pruef.steuersatz')} value={ausgabe.steuersatz}
                             onChange={e => setAusgabe(a => ({ ...a, steuersatz: e.target.value }))}>
                      <option value="19">19 %</option>
                      <option value="7">7 %</option>
                      <option value="0">0 %</option>
                    </Auswahl>
                    <Auswahl label={t('lager.kategorie')} value={ausgabe.kategorie}
                             onChange={e => setAusgabe(a => ({ ...a, kategorie: e.target.value }))}>
                      {KATEGORIEN.map(id => <option key={id} value={id}>{t(KATEGORIE_SCHLUESSEL[id])}</option>)}
                    </Auswahl>
                    <Auswahl label={t('kasse.zahlart')} value={ausgabe.zahlart}
                             onChange={e => setAusgabe(a => ({ ...a, zahlart: e.target.value }))}>
                      <option value="ueberweisung">{t('kasse.ueberweisung')}</option>
                      <option value="lastschrift">{t('pruef.lastschrift')}</option>
                      <option value="karte">{t('kasse.karte')}</option>
                      <option value="bar">{t('kasse.bar')}</option>
                      <option value="privat">{t('pruef.privat')}</option>
                    </Auswahl>
                    <Feld label={t('pruef.belegnummer')} value={ausgabe.belegnummer}
                          onChange={e => setAusgabe(a => ({ ...a, belegnummer: e.target.value }))} />
                    <Feld label={t('pruef.belegdatum')} type="date" value={ausgabe.datum}
                          onChange={e => setAusgabe(a => ({ ...a, datum: e.target.value }))} />
                  </div>
                )}
              </div>
            )}
          </section>

          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        </div>

        {/* Fuß */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('pruef.erstMitKnopf')}
          </p>
          <div className="flex gap-2">
            <Knopf onClick={beiAbbruch} disabled={sendet}>{t('aktion.abbrechen')}</Knopf>
            <Knopf variante="primaer" onClick={buchen} laedt={sendet} disabled={!bereit}>
              {sendet ? t('pruef.wirdGebucht') : t('pruef.buchen')}
            </Knopf>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Eine Belegzeile mit Zuordnung, Menge und Preis. */
function PositionsZeile({
  zeile, artikel, aendern,
}: {
  zeile: ScanPosition;
  artikel: Artikel[];
  aendern: (nr: number, teil: Partial<ScanPosition>) => void;
}) {
  const { t } = useSprache();
  const [offen, setOffen] = useState(zeile.zuordnung !== 'sicher');

  const zuordnungsAbzeichen = zeile.keineWare
    ? <Abzeichen art="neutral">{t('pruef.keineWare')}</Abzeichen>
    : {
      sicher:      <Abzeichen art="gruen">{t('pruef.zugeordnet')}</Abzeichen>,
      vermutet:    <Abzeichen art="gelb">{t('pruef.vermutet')}</Abzeichen>,
      keiner:      <Abzeichen art="neutral">{t('pruef.neu')}</Abzeichen>,
      keinArtikel: null,
    }[zeile.zuordnung];

  return (
    <div className={`rounded-xl border p-3 transition ${
      zeile.uebernehmen
        ? 'border-marke-300 bg-marke-50/50 dark:border-marke-800 dark:bg-marke-950/20'
        : zeile.keineWare
          ? 'border-dashed border-slate-200 opacity-70 dark:border-slate-800'
          : 'border-slate-200 dark:border-slate-800'}`}>

      <div className="flex items-start gap-3">
        <input type="checkbox" checked={zeile.uebernehmen}
               onChange={e => aendern(zeile.nr, {
                 uebernehmen: e.target.checked,
                 // Ohne Treffer heißt „übernehmen" zwangsläufig „anlegen".
                 anlegen: e.target.checked && !zeile.produktId ? true : zeile.anlegen,
               })}
               className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-marke-600"
               aria-label={zeile.bezeichnung} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-900 dark:text-slate-100">{zeile.bezeichnung}</span>
            {zuordnungsAbzeichen}
            {zeile.unsicher && <Abzeichen art="rot">{t('pruef.schlechtLesbar')}</Abzeichen>}
          </div>

          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            {zeile.menge} {zeile.einheit}
            {zeile.einkaufspreis > 0 && ` · ${euro(zeile.einkaufspreis)} ${t('pruef.je')} ${zeile.einheit}`}
            {zeile.artikelnummer && ` · Nr. ${zeile.artikelnummer}`}
          </p>

          {zeile.produktId && !zeile.anlegen && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              → {zeile.produktName}
              {zeile.bestandJetzt !== null && ` · ${t('feld.bestand')} ${zeile.bestandJetzt} → ${zeile.bestandJetzt + zeile.menge}`}
            </p>
          )}
          {zeile.anlegen && (
            <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
              → {t('pruef.wirdAngelegt')}
            </p>
          )}
          {zeile.keineWare && !zeile.uebernehmen && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {t('pruef.keineWareText')}
            </p>
          )}
        </div>

        <button type="button" onClick={() => setOffen(o => !o)}
                className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
          {offen ? t('pruef.zuklappen') : t('pruef.aendern')}
        </button>
      </div>

      {offen && (
        <div className="mt-3 space-y-3 border-t border-slate-200 pt-3 dark:border-slate-800">
          {zeile.zuordnungGrund && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {grundText(t, zeile.zuordnungCode, zeile.zuordnungGrund)}
            </p>
          )}

          <Auswahl
            label={t('pruef.artikel')}
            value={zeile.anlegen ? '__neu' : (zeile.produktId || '')}
            onChange={e => {
              const w = e.target.value;
              if (w === '__neu') aendern(zeile.nr, { anlegen: true, produktId: null });
              else aendern(zeile.nr, { anlegen: false, produktId: w || null });
            }}>
            <option value="">{t('pruef.nichtZugeordnet')}</option>
            <option value="__neu">➕ {t('pruef.alsNeuenArtikel')}</option>
            {artikel.map(a => (
              <option key={a.id} value={a.id}>
                {a.name}{a.artikelnummer ? ` (${a.artikelnummer})` : ''} · {t('feld.bestand')} {a.bestand ?? 0}
              </option>
            ))}
          </Auswahl>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Feld label={t('feld.menge')} inputMode="decimal" value={String(zeile.menge)}
                  onChange={e => aendern(zeile.nr, { menge: Number(e.target.value.replace(',', '.')) || 0 })} />
            <Feld label={t('feld.einheit')} value={zeile.einheit}
                  onChange={e => aendern(zeile.nr, { einheit: e.target.value })} />
            <Feld label={t('pruef.einkaufspreis')} inputMode="decimal" value={String(zeile.einkaufspreis)}
                  onChange={e => aendern(zeile.nr, { einkaufspreis: Number(e.target.value.replace(',', '.')) || 0 })} />
            {zeile.anlegen && (
              <Feld label={t('pruef.verkaufspreis')} inputMode="decimal" value={String(zeile.verkaufspreis ?? '')}
                    hinweis={t('pruef.spaeterErgaenzen')}
                    onChange={e => aendern(zeile.nr, { verkaufspreis: Number(e.target.value.replace(',', '.')) || 0 })} />
            )}
          </div>

          {zeile.anlegen && (
            <div className="grid grid-cols-2 gap-3">
              <Feld label={t('lager.artikelnummer')} value={zeile.artikelnummer}
                    onChange={e => aendern(zeile.nr, { artikelnummer: e.target.value })} />
              <Feld label={t('pruef.ean')} value={zeile.ean}
                    onChange={e => aendern(zeile.nr, { ean: e.target.value })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
